import axios from "axios";
import { hideChildModal, hideModal } from "./modal";

/*****************/
/* INITIAL STATE */
/*****************/
const initialState = {
  loading: false,
  error: "",
  data: [],
};

/*********/
/* TYPES */
/*********/
const FETCH_TENANTS = "FETCH_TENANTS";
const FETCH_TENANTS_SUCCESS = "FETCH_TENANTS_SUCCESS";
const FETCH_TENANTS_ERROR = "FETCH_TENANTS_ERROR";
const FETCH_NEXT_TENANTS = "FETCH_NEXT_TENANTS";
const FETCH_NEXT_TENANTS_SUCCESS = "FETCH_NEXT_TENANTS_SUCCESS";
const DELETE_TENANT = "DELETE_TENANT";
const CREATE_TENANT = "CREATE_TENANT";
const CREATE_TENANT_SUCCESS = "CREATE_TENANT_SUCCESS";
const CREATE_TENANT_ERROR = "CREATE_TENANT_ERROR";
const UPDATE_TENANT = "UPDATE_TENANT";
const UPDATE_TENANT_SUCCESS = "UPDATE_TENANT_SUCCESS";
const UPDATE_TENANT_ERROR = "UPDATE_TENANT_ERROR";

/*******************/
/* ACTION CREATORS */
/*******************/
export const getTenants = () => async (dispatch) => {
  dispatch(loadTenants());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_API_URL}/tenants?page=1`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.data["hydra:member"].length === 0) {
    dispatch(setError("No tenants found"));
  } else {
    dispatch(setTenants(response.data["hydra:member"]));
    if (
      response.data &&
      response.data["hydra:view"]?.["hydra:next"]
    ) {
      dispatch(getNextTenants(response.data["hydra:view"]["hydra:next"]));
    }
  }
};

export const getNextTenants = (nextUrl) => async (dispatch) => {
  dispatch(loadNextTenants());

  const response = await axios({
    method: "get",
    url: `${process.env.REACT_APP_URL}${nextUrl}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  dispatch(setNextTenants(response.data["hydra:member"]));
  if (
    response.data &&
    response.data["hydra:view"]?.["hydra:next"]
  ) {
    dispatch(getNextTenants(response.data["hydra:view"]["hydra:next"]));
  }
};

export const loadTenants = () => ({ type: FETCH_TENANTS });

export const setTenants = (tenants) => ({
  type: FETCH_TENANTS_SUCCESS,
  payload: { tenants },
});

export const loadNextTenants = (page) => ({
  type: FETCH_NEXT_TENANTS,
  payload: page,
});
export const setNextTenants = (tenants) => ({
  type: FETCH_NEXT_TENANTS_SUCCESS,
  payload: tenants,
});

export const setError = (msg) => ({
  type: FETCH_TENANTS_ERROR,
  payload: msg,
});

export const deleteTenantAction = () => ({ type: DELETE_TENANT });

export const removeTenant = (tenant) => async (dispatch, getState) => {
  dispatch(deleteTenantAction());
  const {
    tenants: { data },
    modal: modal
  } = getState();

  const response = await axios({
    method: "delete",
    url: `${process.env.REACT_APP_URL}${tenant["@id"] || `/tenants/${tenant.id}`}`,
    headers: {
      "Content-Type": "application/ld+json; charset=utf-8",
      Authorization: `Bearer ${localStorage.getItem("token")}`,
    },
  });

  if (response.status === 204) {
    const updated = data.filter(
      (t) => t["@id"] !== (tenant["@id"] || `/tenants/${tenant.id}`)
    );

    dispatch(setTenants(updated));
   
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } else {
    dispatch(setError("Something went wrong deleting tenant"));
  }
};

export const createTenant = (tenant, callback) => async (dispatch, getState) => {
  dispatch({ type: CREATE_TENANT });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "post",
      url: `${process.env.REACT_APP_API_URL}/tenants`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: tenant,
    });

    dispatch({
      type: CREATE_TENANT_SUCCESS,
      payload: response.data,
    });

    if (callback) {
      callback("tenants", [response.data["@id"]]);
    }

    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: CREATE_TENANT_ERROR,
      payload: error.message || "Failed to create tenant",
    });
  }
};

export const updateTenant = (tenant) => async (dispatch, getState) => {
  dispatch({ type: UPDATE_TENANT });

  const {
    modal: modal
  } = getState();

  try {
    const response = await axios({
      method: "put",
      url: `${process.env.REACT_APP_URL}${tenant["@id"]}`,
      headers: {
        "Content-Type": "application/ld+json; charset=utf-8",
        Authorization: `Bearer ${localStorage.getItem("token")}`,
      },
      data: tenant,
    });

    dispatch({
      type: UPDATE_TENANT_SUCCESS,
      payload: response.data,
    });
    
    if (modal.childModal) {
      dispatch(hideChildModal());
    } else {
      dispatch(hideModal());
    }
  } catch (error) {
    dispatch({
      type: UPDATE_TENANT_ERROR,
      payload: error.message || "Failed to update tenant",
    });
  }
};

/***********/
/* REDUCER */
/***********/
const tenantsReducer = (state = initialState, { type, payload }) => {
  switch (type) {
    case FETCH_TENANTS:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case FETCH_TENANTS_SUCCESS:
      return {
        ...state,
        loading: false,
        error: "",
        data: payload.tenants,
      };
    case FETCH_TENANTS_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
    case FETCH_NEXT_TENANTS:
      return {
        ...state,
        error: "",
      };
    case FETCH_NEXT_TENANTS_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [...state.data, ...payload],
      };
    case DELETE_TENANT:
      return {
        ...state,
        loading: true,
        error: "",
      };
    case CREATE_TENANT:
      return {
        ...state,
        loading: true,
        error: "",
      };

    case CREATE_TENANT_SUCCESS:
      return {
        ...state,
        loading: false,
        data: [payload, ...state.data],
      };

    case CREATE_TENANT_ERROR:
      return {
        ...state,
        loading: false,
        error: payload,
      };
     case UPDATE_TENANT:
       return {
         ...state,
         loading: true,
         error: "",
       };

     case UPDATE_TENANT_SUCCESS:
       return {
        ...state,
        loading: false,
        data: state.data.map((t) =>
          t["@id"] === payload["@id"] ? payload : t
        ),
      };

     case UPDATE_TENANT_ERROR:
       return {
         ...state,
         loading: false,
         error: payload,
       };
    default:
      return state;
  }
};

export default tenantsReducer;
